import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PricingTypesSelect } from 'components/AsyncFields';

import {
  Box,
  Grid,
  Paper,
  Button,
  Container,
  IconButton,
  Typography,
  Tooltip,
} from '@material-ui/core';
import i18n from 'i18n-js';
import Can from 'components/Can';
import { useQuery } from 'hooks';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import { cloneDeep, omit } from 'lodash';
import useStyles from './SkusList.styled';
import { useHistory } from 'react-router-dom';
import Switch from '@material-ui/core/Switch';
import EditIcon from '@material-ui/icons/Edit';
import { SkusActions } from 'state/modules/skus';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import { truncateKeywordSpaces } from 'utils/utils';
import ArchiveIcon from '@material-ui/icons/Archive';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import { SkuBehaviourSelect } from 'components/DataFields';
import { buildUrl, capitalize, toQueryString } from 'utils';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { debounce } from 'lodash';

const SkusList = () => {
  const history = useHistory();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const classes = useStyles();
  const urlParams = useQuery();
  const dispatch = useDispatch();

  const items = useSelector((state) => state.skus.items);
  const loading = useSelector((state) => state.skus.loading);
  const pagination = useSelector((state) => state.skus.pagination);
  const fetchedDeleteSku = useSelector((state) => state.skus.fetchedDeleteSku);
  const fetchedUpdateSku = useSelector((state) => state.skus.fetchedUpdateSku);
  const fetchingDeleteSku = useSelector((state) => state.skus.fetchingDeleteSku);
  const fetchingUpdateSku = useSelector((state) => state.skus.fetchingUpdateSku);

  const refetch = fetchedDeleteSku || fetchedUpdateSku;

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    active: false,
    ...urlParams,
  });

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/skus/${row?.id}`));
  };

  const columns = [
    {
      name: i18n.t('tableColumns.id'),
      selector: 'id',
      sortable: true,
    },
    {
      name: i18n.t('tableColumns.name'),
      selector: 'name',
      grow: 2,
      sortable: true,
    },
    {
      name: i18n.t('skus.skus_type'),
      grow: 2,
      sortable: true,
      selector: 'pricing_type',
      cell: (row) => row?.pricing_type?.external_name ?? '--',
    },
    {
      name: i18n.t('skus.units'),
      sortable: true,
      selector: 'units.total',
      cell: (row) => (row?.units?.total ? capitalize(row.units.total) : '--'),
    },
    {
      name: i18n.t('tableColumns.behaviour'),
      grow: 3,
      selector: 'manual',
      cell: (row) =>
        row.auto && row.manual
          ? i18n.t('skus.automatic') + ' / ' + i18n.t('skus.manual')
          : row.auto
          ? i18n.t('skus.automatic')
          : row.manual
          ? i18n.t('skus.manual')
          : '--',
    },
    {
      name: i18n.t('common.status'),
      sortable: true,
      selector: 'status',
      cell: (row) => (
        <div className={`${classes.status} ${classes[row.active ? 'active' : 'inactive']}`}>
          <span className="status">
            {row.active ? i18n.t('status.ACTIVE') : i18n.t('common.INACTIVE')}
          </span>
        </div>
      ),
    },
    {
      name: i18n.t('tableColumns.action'),
      cell: (row) => (
        <>
          <Can I="update" on="invoice_item_type">
            <Tooltip title={i18n.t('skus.update_sku')}>
              <IconButton aria-label="edit" onClick={() => handleRowClick(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Can>
          {row.active ? (
            <Can I="delete" on="invoice_item_type">
              <Tooltip title={i18n.t('skus.delete_sku')}>
                <IconButton
                  disabled={fetchingDeleteSku}
                  aria-label="archive"
                  onClick={() => deleteSku(row)}
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Can>
          ) : (
            <Can I="update" on="invoice_item_type">
              <Tooltip title={i18n.t('skus.activate_sku')}>
                <IconButton
                  disabled={fetchingUpdateSku}
                  aria-label="unarchive"
                  onClick={() => updateSku(row)}
                >
                  <UnarchiveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Can>
          )}
        </>
      ),
      ignoreRowClick: true,
      button: true,
      center: true,
      grow: 0,
    },
  ];

  const fetchSkus = useCallback(
    (data = {}) => {
      let _params = cloneDeep(data);

      if (_params.active === false) {
        _params.status = 'ACTIVE';
      } else {
        delete _params.status;
      }

      if (_params.behaviour) delete _params.behaviour;

      if (_params?.keyword) {
        _params.keyword = truncateKeywordSpaces(_params.keyword);
      }

      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        SkusActions.fetchSkus(omit(_params, 'active'), { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchSkusDebounced = useCallback(
    debounce((params) => fetchSkus(params), 1000),
    [fetchSkus]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchSkusDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchSkusDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchSkus(params);
  }, []);

  // On refetch delete/update success no debounce
  useEffect(() => {
    if (refetch) {
      fetchSkus(params);
    }
  }, [fetchSkus, refetch]);

  const handleParams = (params) => {
    setParams(params);
    const _params = toQueryString(params);
    history.replace(buildUrl(`/skus${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      page: 1,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Delete SKU
   * @param {object} row Data of the row selected
   */
  const deleteSku = (row) => {
    if (row?.id) dispatch(SkusActions.fetchDeleteSku(row?.id));
  };

  /**
   * Update SKU
   * @param {object} row Data of the row selected
   */
  const updateSku = (row) => {
    if (row?.id) dispatch(SkusActions.fetchUpdateSku(row?.id, { status: 'ACTIVE' }));
  };

  // search filter
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };

    if (name === 'pricing_types') {
      filters.pricing_types = value?.length > 0 ? value.map((val) => val?.name || val) : undefined;
    }

    if (name === 'behaviour') {
      delete filters.manual;
      delete filters.auto;

      if (value === 'manual') {
        filters.manual = true;
      }
      if (value === 'automatic') {
        filters.auto = true;
      }
    }

    // remove empty fields
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && delete filters[key]
    );
    handleParams(filters);
  };

  return (
    <Layout title={i18n.t('skus.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="fontWeightBold" fontSize={20} data-cy="title" my={1}>
                    {i18n.t('skus.title')}
                  </Box>
                </Typography>
              </Grid>
              <Grid item>
                <Can I="create" a="invoice_item_type">
                  <Button
                    onClick={() => {
                      dispatch(SkusActions.resetOnboarding());
                      history.push(buildUrl('/skus/create'));
                    }}
                    data-cy="create-sku"
                    style={{ textTransform: 'capitalize' }}
                    variant="contained"
                    color="primary"
                  >
                    {i18n.t('skus.create')}
                  </Button>
                </Can>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ paddingBottom: '25px' }}>
            <Grid
              container
              spacing={2}
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
            >
              <Grid item xs={12} sm={4} md={3}>
                <SearchField
                  label={i18n.t('common.search')}
                  margin="none"
                  field={{
                    name: 'keyword',
                    onChange: search,
                    value: params.keyword,
                  }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <PricingTypesSelect
                  label={i18n.t('skus.skus_type')}
                  type="sku"
                  margin="none"
                  defaultParams={{
                    page: 1,
                    limit: 20,
                  }}
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'pricing_types',
                    value: params.pricing_types,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <SkuBehaviourSelect
                  label={i18n.t('skus.behaviour')}
                  margin="none"
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'behaviour',
                    value: params.behaviour || '',
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControlLabel
                  value="start"
                  className={classes.switchLabel}
                  control={
                    <Switch
                      name="active"
                      color="primary"
                      onChange={(_, value) => search('active', value)}
                      checked={
                        typeof params.active === 'boolean'
                          ? params.active
                          : params.active === 'true'
                          ? true
                          : false
                      }
                    />
                  }
                  label={i18n.t('common.include_archived')}
                  labelPlacement="start"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2}>
              <Table
                data={items}
                columns={columns}
                loading={loading}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default SkusList;
